import React, { useEffect, useMemo, useState } from 'react';
import { GatewayData } from '../types';
import { fetchExportData, convertGatewayDataToCSV } from '../lib/supabaseDataProcessing';

interface ExportDropdownProps {
  gatewayData: GatewayData[]; // current latest gateway data
  // optional: callback when export starts/ends
  onExportStarted?: () => void;
  onExportFinished?: (filename?: string) => void;
}

const ExportDropdown: React.FC<ExportDropdownProps> = ({ gatewayData, onExportStarted, onExportFinished }) => {
  const [open, setOpen] = useState(false);
  const [selectedDtus, setSelectedDtus] = useState<string[]>([]);
  const [mode, setMode] = useState<'days' | 'latest'>('days');
  const [days, setDays] = useState<number>(7);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const availableDtus = useMemo(() => {
    const dtus = gatewayData.map(g => ({ id: g.gateway_id, name: g.alias || g.gateway_id }));
    // unique and sorted by name
    const map = new Map<string, { id: string; name: string }>();
    dtus.forEach(d => map.set(d.id, d));
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [gatewayData]);

  useEffect(() => {
    // default selected = all
    setSelectedDtus(availableDtus.map(d => d.id));
  }, [availableDtus.length]);

  // close on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return;
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  // toggle selection; accept optional event to stop propagation so clicks inside the dropdown don't bubble to document
  const toggleDtu = (id: string, e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    setSelectedDtus(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Provide a sorted view of the selected DTUs by name for display
  const sortedSelectedDtus = useMemo(() => {
    return selectedDtus.slice().sort((a, b) => {
      const an = availableDtus.find(x => x.id === a)?.name || a;
      const bn = availableDtus.find(x => x.id === b)?.name || b;
      return an.localeCompare(bn);
    });
  }, [selectedDtus, availableDtus]);

  const clearSelection = () => setSelectedDtus([]);
  const selectAll = () => setSelectedDtus(availableDtus.map(d => d.id));

  const handleExport = async () => {
    try {
      onExportStarted && onExportStarted();
      // Choose source depending on mode
      if (mode === 'days') {
        // Fetch historical records for selected DTUs
        const clampedDays = Math.max(1, Math.min(28, Number(days) || 1));
        setDays(clampedDays);
        const data = await fetchExportData(selectedDtus, { type: 'days', days: clampedDays }, gatewayData);
        const csv = convertGatewayDataToCSV(data);
        downloadCsv(csv, `export_days_${clampedDays}_${new Date(Date.now() + 28800000).toISOString()}.csv`);
        onExportFinished && onExportFinished(`export_days_${clampedDays}.csv`);
      } else {
        // latest one for each DTU - we can either call backend helper or use provided gatewayData
        const data = await fetchExportData(selectedDtus, { type: 'latest' }, gatewayData);
        const csv = convertGatewayDataToCSV(data);
        downloadCsv(csv, `export_latest_${new Date(Date.now() + 28800000).toISOString()}.csv`);
        onExportFinished && onExportFinished(`export_latest.csv`);
      }
    } catch (err) {
      console.error('Export failed', err);
      onExportFinished && onExportFinished();
    }
  };

  const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center px-3 py-1 border rounded-md bg-white hover:bg-gray-50 text-sm active:bg-gray-200"
        aria-haspopup="true"
        aria-expanded={open}
        title="Export selected data"
      >
        <span className="text-sm">Export</span>
      </button>

      {open && (
        <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 p-3">
          <div className="mb-3">
            <div className="font-medium mb-2">Selected DTUs</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {sortedSelectedDtus.map(id => {
                const d = availableDtus.find(a => a.id === id);
                return (
                  <span key={id} className="inline-flex items-center px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                    {d ? d.name : id}
                    <button type="button" onClick={(e) => toggleDtu(id, e)} className="ml-2 text-gray-500 hover:text-gray-700 active:text-black">✕</button>
                  </span>
                );
              })}
            </div>

            <div className="flex gap-2 mb-2">
              <button onClick={clearSelection} className="px-2 py-1 text-sm border rounded hover:bg-gray-50 active:bg-gray-100 transition">Clear</button>
              <button onClick={selectAll} className="px-2 py-1 text-sm border rounded hover:bg-gray-50 active:bg-gray-100 transition">All</button>
            </div>

            <div className="max-h-28 overflow-auto border rounded p-1">
              {availableDtus.map(d => (
                <label key={d.id} className="flex items-center text-sm py-0.5">
                  <input type="checkbox" checked={selectedDtus.includes(d.id)} onChange={(e) => toggleDtu(d.id, e)} className="mr-2" />
                  <span>{d.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <div className="font-medium mb-2">Time Range</div>
            <div className="flex items-center gap-3 mb-2">
              <label className="text-sm">
                <input type="radio" name="exportMode" checked={mode === 'days'} onChange={() => setMode('days')} className="mr-2" />
                Days
              </label>
              <input type="number" min={1} max={28} value={days} onChange={(e) => setDays(Math.max(1, Math.min(28, Number(e.target.value) || 1)))} className="w-20 border rounded px-2 py-1 text-sm" />
              <span className="text-sm text-gray-500">(1-28)</span>
            </div>

            <div>
              <label className="text-sm">
                <input type="radio" name="exportMode" checked={mode === 'latest'} onChange={() => setMode('latest')} className="mr-2" />
                Latest 1 of each Selected DTU
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => { setOpen(false); }} className="px-3 py-1 border rounded hover:bg-gray-50 active:bg-gray-100 transition">Close</button>
            <button onClick={handleExport} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 active:bg-blue-800 transition">Export</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
